import { isDefined } from '@sgme/fp';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useAlgoTileId, useValidateAlgo } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectAlgoErrors, selectTradeIdFromAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import { selectOrderEntryByIdFromBlotter } from '@/store/orders/fixing/selectors.ts';

type AlgoClipSizeProps = {
  clipSize: number | undefined;
  setClipSize: (value: number | undefined) => void;
};

export const AlgoEditingClipSize = (props: AlgoClipSizeProps) => {
  const { clipSize, setClipSize } = props;
  const tileId = useAlgoTileId();
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const clipSizeError = errors?.clipSize;

  const validateAlgo = useValidateAlgo();

  const handleClipSizeChange = async (value: number | undefined) => {
    track('Order action', 'Clip size changed');
    if (isDefined(orderFromBlotter)) {
      const response = await validateAlgo({
        clipSize: value,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setClipSize(value);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.clipSize">
      <OmsFieldErrorWrapper errors={clipSizeError}>
        <SgNumberInput
          valuePosition="start"
          size="sm"
          maxDecimals={9}
          value={
            clipSize ?? (isDefined(orderFromBlotter?.clipSize) ? (orderFromBlotter?.clipSize as number) : undefined)
          }
          data-e2e="clipSize"
          onChange={handleClipSizeChange}
          selectAllOnFocus
          state={isDefined(clipSizeError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
